import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const vehicleTypeSchema = (t: any) =>
  z.object({
    name: z.string().min(1, t("vehicleTypes.form.validation.nameRequired")),
  });

type VehicleTypeFormValues = z.infer<typeof vehicleTypeSchema>;

interface VehicleTypeFormProps {
  id?: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const VehicleTypeForm = ({
  id,
  onSuccess,
  onCancel,
}: VehicleTypeFormProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const form = useForm<VehicleTypeFormValues>({
    resolver: zodResolver(vehicleTypeSchema(t)),
    defaultValues: {
      name: "",
    },
  });

  // Fetch supplier data if in edit mode
  useQuery({
    queryKey: ["vehicle-type", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`vehicle-types/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(t("vehicleTypes.error.fetchSingle"));
      }
      const data = await response.json();
      form.reset(data);
      return data;
    },
    enabled: isEdit && !!id,
  });

  const mutation = useMutation({
    mutationFn: async (data: VehicleTypeFormValues) => {
      const url = isEdit ? `vehicle-types/${id}/` : "vehicle-types/";
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t("vehicleTypes.error.save"));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-types"] });
      onSuccess();
    },
  });

  const onSubmit = async (data: VehicleTypeFormValues) => {
    try {
      await mutation.mutateAsync(data);
    } catch (error) {
      // Error is handled by the mutation's onError
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("vehicleTypes.form.name")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("vehicleTypes.form.namePlaceholder")}
                  {...field}
                  disabled={mutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? t("common.saving")
              : isEdit
                ? t("common.update")
                : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
